import { Routes } from '@angular/router';
import { adminGuard, appAccessGuard, authGuard, loginGuard, platformAdminGuard } from './core/auth/auth.guard';
import { unsavedChangesGuard } from './shared/ui/unsaved-changes.guard';
import { CAPEX_APP_CODE } from './core/models/capex.models';
import { Shell } from './layout/shell/shell';
import { Login } from './pages/login/login';
import { SetPassword } from './pages/set-password/set-password';

/**
 * Administration pages, each loaded when first opened. App admins use People, App access, Forms and Workflows for
 * their applications; the rest is for administrators only.
 */
const ADMIN_ROUTES: Routes = [
  { path: '', pathMatch: 'full', redirectTo: 'organisation' },
  { path: 'organisation', canActivate: [platformAdminGuard], loadComponent: () => import('./features/admin/organisation/organisation.page').then((m) => m.OrganisationPage) },
  { path: 'people', loadComponent: () => import('./features/admin/people/people.page').then((m) => m.PeoplePage) },
  { path: 'access', loadComponent: () => import('./features/admin/access/access-setup.page').then((m) => m.AccessSetupPage) },
  { path: 'forms', canDeactivate: [unsavedChangesGuard], loadComponent: () => import('./features/admin/forms/forms.page').then((m) => m.FormsPage) },
  { path: 'workflows', canDeactivate: [unsavedChangesGuard], loadComponent: () => import('./features/admin/workflows/workflows.page').then((m) => m.WorkflowsPage) },
  { path: 'applications', canActivate: [platformAdminGuard], loadComponent: () => import('./features/admin/applications/applications.page').then((m) => m.ApplicationsPage) },
  { path: 'org-import', canActivate: [platformAdminGuard], loadComponent: () => import('./features/admin/org-import/org-import.page').then((m) => m.OrgImportPage) },
];

export const routes: Routes = [
  { path: 'login', component: Login, canActivate: [loginGuard] },
  { path: 'set-password', component: SetPassword, canActivate: [authGuard] },
  {
    path: '',
    component: Shell,
    canActivate: [authGuard],
    children: [
      { path: '', loadComponent: () => import('./features/launcher/home.page').then((m) => m.HomePage) },
      { path: 'inbox', loadComponent: () => import('./features/inbox/inbox.page').then((m) => m.InboxPage) },
      { path: 'notifications', loadComponent: () => import('./features/notifications/notifications.page').then((m) => m.NotificationsPage) },
      // A pending decision, and a request's page; both are opened from the lists (see RequestDialogs).
      { path: 'approvals/:taskId', loadComponent: () => import('./shared/request/approval.page').then((m) => m.ApprovalPage) },
      { path: 'requests/:requestId', loadComponent: () => import('./shared/request/request-detail.page').then((m) => m.RequestDetailPage) },
      {
        path: 'capex',
        canActivate: [appAccessGuard],
        data: { appCode: CAPEX_APP_CODE },
        children: [
          { path: '', loadComponent: () => import('./features/capex/capex.page').then((m) => m.CapexPage) },
          // The requisition form is long, so it is a page of its own (new, or a draft / returned request).
          { path: 'new', canDeactivate: [unsavedChangesGuard], loadComponent: () => import('./features/capex/capex-request.page').then((m) => m.CapexRequestPage) },
          { path: 'edit/:id', canDeactivate: [unsavedChangesGuard], loadComponent: () => import('./features/capex/capex-request.page').then((m) => m.CapexRequestPage) },
        ],
      },
      {
        path: 'apps/:appCode',
        canActivate: [appAccessGuard],
        loadComponent: () => import('./features/requests/app.page').then((m) => m.AppPage),
      },
      { path: 'admin', canActivate: [adminGuard], children: ADMIN_ROUTES },
      { path: 'no-access', loadComponent: () => import('./pages/no-access/no-access').then((m) => m.NoAccess) },

      // Old addresses
      { path: 'organisation', redirectTo: 'admin/organisation' },
      { path: 'workflow', redirectTo: 'admin/workflows' },
      { path: 'workflow/instances', redirectTo: 'inbox' },
    ],
  },
  { path: '**', redirectTo: '' },
];
